import { InterstitialPage, urlUtil } from '@cmg/common';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate, useLocation } from 'react-router-dom';

import routeFactory from '../../../common/util/routeFactory';
import { ErrorCode } from '../error/constants';
import ConsentUnavailable from './components/ConsentUnavailable';
import ConsentScreen from './ConsentScreen';
import {
  fetchConsentInfo,
  selectConsentInfo,
  selectPendingDecision,
  selectStatus,
  submitConsent,
} from './ducks';

/**
 * The OAuth consent screen. Duende sends a signed-in user here with the authorize callback as
 * `returnUrl`; that string is passed back to the API verbatim on both calls and never parsed here.
 *
 * Both calls, the decision's navigation and the state they leave behind live in `ducks.ts`. This
 * renders whichever screen the status names and dispatches the two things the user can do.
 */
export const ConsentRoute: React.FC = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { returnUrl } = urlUtil.queryParse<{ returnUrl?: string }>(location.search);

  const status = useSelector(selectStatus);
  const consentInfo = useSelector(selectConsentInfo);
  const pendingDecision = useSelector(selectPendingDecision);

  React.useEffect(() => {
    if (returnUrl) {
      dispatch(fetchConsentInfo({ returnUrl }));
    }
  }, [dispatch, returnUrl]);

  const submit = React.useCallback(
    (granted: boolean, rememberConsent: boolean) => {
      if (!returnUrl) {
        return;
      }

      dispatch(submitConsent({ returnUrl, granted, rememberConsent }));
    },
    [dispatch, returnUrl]
  );

  // Landing here without a returnUrl means the user did not come from an authorize request.
  if (!returnUrl) {
    return <Navigate to={routeFactory.profile.getUrlPath()} replace />;
  }

  if (status === 'failed') {
    return (
      <Navigate
        to={routeFactory.error.getUrlPath({ errorCode: ErrorCode.CONSENT_PREREQUISITE })}
        replace
      />
    );
  }

  if (status === 'expired') {
    return <ConsentUnavailable clientName={consentInfo?.clientName} />;
  }

  if (status === 'loading' || !consentInfo) {
    return <InterstitialPage />;
  }

  return (
    <ConsentScreen
      consentInfo={consentInfo}
      pendingDecision={pendingDecision}
      onGrant={rememberConsent => submit(true, rememberConsent)}
      onDeny={() => submit(false, false)}
    />
  );
};

export default ConsentRoute;
